import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, map, pairwise } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import type { AOA2SheetOpts, WorkBook, WorkSheet } from 'xlsx';

import { HighlightOverlayBubblesComponent } from '@common/listing-preview/highlight-overlay-bubbles/highlight-overlay-bubbles.component';
import { CheckPlatformService } from './helpers/check-platform.service';
import { Crypto } from './helpers/encryption-utils';
import { LocalStorageService } from './helpers/local-storage.service';
import { isPublicReportLocation } from './helpers/public-report-route';
import { ActivityTrackerService } from './user-activity/activity-tracker.service';

@Injectable({
  providedIn: 'root',
})
export class SharedDataService {
  public baseUrl = environment.nodeApiUrl + '/' + environment.api_prefix;
  public url = environment.nodeApiUrl;

  constructor(
    private http: HttpClient,
    private router: Router,
    private localStorageService: LocalStorageService,
    private checkPlatformService: CheckPlatformService,
    private activityTracker: ActivityTrackerService
  ) {}

  private approvalNotificationUpadted = new Subject();
  approvalNotificationUpdatedObservable = this.approvalNotificationUpadted.asObservable();
  public overlayBubble: HighlightOverlayBubblesComponent;

  private messageSource = new BehaviorSubject(JSON.stringify({ cmd: '' }));

  currentMessage = this.messageSource.asObservable();

  private libraryGetDataHandler = new Subject();
  libraryGetDataHandlerObservable = this.libraryGetDataHandler.asObservable();

  private updateProject = new Subject();
  projectDetailed = this.updateProject.asObservable();

  private routingUrlPath = new BehaviorSubject('');
  UrlPathObservable = this.routingUrlPath.asObservable();

  public simplelibtype = new BehaviorSubject('userOffice');
  public reLoadLibrary = new BehaviorSubject('');

  /*- CUSTOM FORM START-******/
  public customformfields: any = {};
  public customformfieldsinfo: any = {};
  public customformlayout: any = null;

  public customformfieldsChange(_event) {
    // Method body intentionally left empty after removing console.info statements
  }
  /*- CUSTOM FORM ENDS-******/
  /*******/

  /*- FORM ELEMENT SPECIFIC START-******/
  public entities: any = [];
  public entitiesList: any = [];
  public officeid: any = null;

  setOverlayBubbleComponent(overlayBubble: HighlightOverlayBubblesComponent) {
    this.overlayBubble = overlayBubble;
  }

  public customfieldlookupdata: any[] = [];
  /*- FORM ELEMENT SPECIFIC START-******/
  /*******/

  getUrlStatus(): Observable<string> {
    return this.router.events.pipe(
      filter(evt => evt instanceof RoutesRecognized),
      pairwise(),
      map((events: [RoutesRecognized, RoutesRecognized]) => {
        return JSON.stringify({ 'previous-url': events?.[0]?.urlAfterRedirects, 'current-url': events?.[1]?.urlAfterRedirects });
      })
    );
  }
  approvalNotificationUpdateEventHandler(status: any) {
    this.approvalNotificationUpadted.next(status);
  }
  triggerFuncEventHandler(data: any) {
    this.libraryGetDataHandler.next(data);
  }
  updateProjectDetails(data: any) {
    this.updateProject.next(data);
  }
  changeMessage(message: string) {
    this.messageSource.next(message);
  }

  getUserId() {
    return Crypto.decrypt(this.localStorageService.GetItem('uid'));
  }

  getOfficeId() {
    return Crypto.decrypt(this.localStorageService.GetItem('officeid'));
  }

  /*******************************************************/
  /**-------------------- API CALLS --------------------**/
  /*******************************************************/

  get(url, params = null): Observable<any> {
    if (params) {
      return this.http
        .get(this.baseUrl + url, {
          params,
        })
        .pipe(map(this.handleResponse), catchError(this.handleError));
    }
    return this.http.get(this.baseUrl + url).pipe(map(this.handleResponse), catchError(this.handleError));
  }

  post(url, params, priority: 'low' | 'medium' | 'high' = 'high'): Observable<any> {
    let httpPriorityOption = {
      headers: new HttpHeaders({ Priority: priority }),
    };
    return this.http.post(this.baseUrl + url, params, { ...httpPriorityOption }).pipe(map(this.handleResponse), catchError(this.handleError));
  }

  put(url, params): Observable<any> {
    return this.http.put(this.baseUrl + url, params).pipe(map(this.handleResponse), catchError(this.handleError));
  }

  delete(url): Observable<any> {
    return this.http.delete(this.baseUrl + url).pipe(map(this.handleResponse), catchError(this.handleError));
  }
  getwithpromise(url, params = null): Promise<any> {
    let httpPriorityOption = {
      headers: new HttpHeaders({ Priority: 'low' }),
    };
    if (params) {
      return this.http.get(this.baseUrl + url, { ...httpPriorityOption, params: params }).toPromise();
    }
    return this.http.get(this.baseUrl + url, { ...httpPriorityOption }).toPromise();
  }
  public handleResponse(data) {
    // Mirrors the exemption in `ApiService.handleResponse`, which this copy was missing. Without
    // it, ANY call through this service that came back with an in-body `status_code: 401` hard
    // navigated the browser to `/#/login` — a full page load that no router guard can veto. On a
    // public `/report-view/:slug` link (emailed to customers with no Remmi account) that is the
    // reported "it auto redirects to the Remmi login page".
    //
    // NOTE: this method is passed as a bare function reference to `map(this.handleResponse)`, so
    // `this` is undefined at call time — the check must not touch instance state.
    if (data && data.status_code && data.status_code === 401 && !isPublicReportLocation()) {
      window.location.href = '/#/login';
    }
    return data;
  }

  public handleError(error) {
    return throwError(() => error);
  }

  // xlsx is loaded on demand (here and below) so it stays out of the initial bundle.
  public async importXlsx(bstr: string): Promise<AOA2SheetOpts> {
    const { read, utils } = await import('xlsx');
    /* read workbook */
    const wb: WorkBook = read(bstr, { type: 'binary' });

    /* grab first sheet */
    const wsname: string = wb.SheetNames[0];
    const ws: WorkSheet = wb.Sheets[wsname];

    /* save data */
    const data = <AOA2SheetOpts>utils.sheet_to_json(ws, { header: 1 });

    return data;
  }
  public async ConvertToCSV(bstr: string): Promise<string> {
    const { read, utils } = await import('xlsx');
    /* read workbook */
    const wb: WorkBook = read(bstr, { type: 'binary' });

    /* grab first sheet */
    const wsname: string = wb.SheetNames[0];
    const ws: WorkSheet = wb.Sheets[wsname];

    /* save data */
    const data = utils.sheet_to_json(ws, { header: 1 });
    let header: any = data.shift();
    header.forEach((itm, idx) => {
      header[idx] = itm.toString().toLowerCase().trim().replace(/  /g, ' ').replace(/ /g, '_');
    });

    let csvstring = '';
    csvstring = `${header.toString()}\n`;

    data.forEach((item: any) => {
      let havedata = false;
      item.forEach((n, idx) => {
        if (n.toString() != '') {
          havedata = true;
        }
        if (n.toString().indexOf(',')) {
          item[idx] = `"${n}"`;
        }
      });

      if (havedata) {
        csvstring += `${item.toString()}\n`;
      }
    });

    return csvstring;
  }
  public async importFromFile(bstr: string) {
    const { read, utils } = await import('xlsx');
    /* read workbook */
    const wb: WorkBook = read(bstr, { type: 'binary' });

    /* grab first sheet */
    const wsname: string = wb.SheetNames[0];
    const ws: WorkSheet = wb.Sheets[wsname];

    /* save data */
    const data = utils.sheet_to_json(ws, { header: 1 });

    let header: any = data.shift();
    header.forEach(itm => {
      itm = itm.toString().toLowerCase().trim().replace(/  /g, ' ').replace(/ /g, '_');
    });
    let datafeed = [];

    data.forEach((itm: any) => {
      let item = {};
      itm.forEach((fld, idx) => {
        item[header[idx]] = fld;
      });
      datafeed.push(item);
    });
    return datafeed;
  }

  public async exportTableToFile(fileName: string, element_id: string) {
    if (!element_id) throw new Error('Element Id does not exists');
    const { utils, writeFile } = await import('xlsx');

    let tbl = this.checkPlatformService.isBrowser() ? document.getElementById(element_id) : null;
    let wb = utils.table_to_book(tbl);
    writeFile(wb, fileName + '.xlsx');
  }

  public async exportArrayToExcel(arr: any[], fileName?: string) {
    const { utils, writeFile } = await import('xlsx');
    var wb = utils.book_new();
    var ws = utils.json_to_sheet(arr);
    ws['!autofilter'] = { ref: 'A1:Z4' };
    utils.book_append_sheet(wb, ws, fileName);
    writeFile(wb, `${fileName}.xlsx`);
  }

  public async exportToExcel(arr: any[], fileName?: string, entityid?) {
    this.activityTracker.track('report_export', { meta: { format: 'xlsx', module: entityid || undefined } });
    let record = [];

    if (entityid == 'eoi') {
      record = arr?.map(ele => {
        ele = {
          ...ele,
          carpark_number: ele?.carpark_number ? ele?.carpark_number?.map(cpn => cpn?.carpark_lot) : [],
          carpark_storage: ele?.carpark_storage ? ele?.carpark_storage?.map(cps => cps?.storage_no) : [],
        };
        return ele;
      });
    } else {
      record = arr;
    }

    let payload = {
      module: entityid,
      office_id: this.getOfficeId(),
      user_id: this.getOfficeId(),
    };

    let lookups = await this.http.post(`${this.baseUrl}/collections/fields-by-module`, payload).toPromise();
    let fields = lookups['data']['fields'];
    fields.unshift({
      _id: 99999999999999999,
      label: '_id',
      name: '_id',
      description: '',
      format: 1,
      length: '',
      options: {
        entity: null,
        entityid: null,
        label_field: null,
        value_field: null,
      },
    });

    let projectid_index = null;
    fields.forEach((itm, idx) => {
      if (itm.name == 'projectid') {
        projectid_index = idx;
        return;
      }
    });
    if (projectid_index != null) {
      fields.splice(projectid_index, 1);
    }
    lookups = lookups['data'];

    // Loaded on demand so exceljs stays out of the initial bundle.
    const { Workbook } = await import('exceljs');
    const workbook = new Workbook();
    workbook.creator = 'Me';
    workbook.lastModifiedBy = 'Me';
    workbook.created = new Date();
    workbook.modified = new Date();
    workbook.lastPrinted = new Date();
    // Set workbook dates to 1904 date system
    workbook.properties.date1904 = true;
    // Force workbook calculation on load
    workbook.calcProperties.fullCalcOnLoad = true;
    workbook.views = [
      {
        x: 0,
        y: 0,
        width: 10000,
        height: 20000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ];
    const worksheet = workbook.addWorksheet('My Sheet', { views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }] });
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: fields.length },
    };
    let tcolumns = [];
    let tfields = [...fields];
    let tidc = tfields.shift();
    tcolumns.push({ header: tidc.name, key: tidc.name, width: 10, hidden: false });
    tfields.forEach(fld => {
      tcolumns.push({ header: fld.name, key: fld.name.toLowerCase(), width: fld.name.toString().length * 2 });
      // if (fld?.name !== 'price_m2') {
      // }
    });

    worksheet.columns = tcolumns;
    tcolumns?.forEach(el => {
      if (el?.key == 'total' || el?.key == 'total_area' || el?.key == 'lot_price' || el?.key == 'external_area' || el?.key == 'internal_area') {
        worksheet.getColumn(el?.key).numFmt = '"$"#,##0.00';
      }
      if (el?.key == 'bed' || el?.key == 'bath') {
        worksheet.getColumn(el?.key).numFmt = '0';
      } else {
        worksheet.getColumn(el?.key).numFmt = '@';
      }
    });
    function isValidJSON(str) {
      try {
        JSON.parse(str);
        return true;
      } catch (e) {
        return false;
      }
    }
    record.forEach(itm => {
      let trc = {};
      fields.forEach(fld => {
        if (itm?.[fld?.name] && fld?.name?.toLowerCase() === 'type') {
          trc[fld.name] =
            typeof itm?.[fld?.name] === 'object' || (isValidJSON(itm?.[fld?.name]) && typeof JSON.parse(itm?.[fld?.name]) === 'object')
              ? itm?.[fld?.name]?.name || ''
              : itm?.[fld?.name];
        } else if (itm[fld.name]) {
          trc[fld.name] = itm[fld.name];
        } else {
          trc[fld.name] = '';
        }
      });
      worksheet.addRow(trc);
      var row = worksheet.lastRow;
      Object.keys(trc).forEach(key => {
        if (lookups[key]?.length) {
          if (lookups[key]?.toString().length < 250) {
            row.getCell(key).dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: ['"' + lookups[key].toString() + '"'],
            };
          }
        }
      });
    });
    let trc = {};
    fields.forEach(fld => {
      trc[fld.name] = '';
    });
    trc['_id'] = '';
    console.info(trc);
    for (let i = 0; i < 1000; i++) {
      worksheet.addRow(trc);
      var row = worksheet.lastRow;
      Object.keys(trc).forEach(key => {
        if (lookups[key]?.length) {
          if (lookups[key]?.toString().length < 250) {
            row.getCell(key).dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: [`"${lookups[key].toString()}"`],
            };
          }
        }
      });
    }
    const buffer = await workbook.xlsx.writeBuffer();
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const fileExtension = '.xlsx';
    const blob = new Blob([buffer], { type: fileType });
    const { saveAs } = await import('file-saver');
    saveAs(blob, fileName + fileExtension);
  }

  say(_message) {
    return true;
  }
  _say(message) {
    var msg = new SpeechSynthesisUtterance(message);
    window.speechSynthesis.speak(msg);
  }
  __say(message) {
    let chk = this.localStorageService.GetItem('justsaid');
    if (Crypto.decrypt(chk)) return;

    var msg = new SpeechSynthesisUtterance();
    msg.voice = speechSynthesis.getVoices().filter(function (voice) {
      return voice.name.toString().toLowerCase() == 'microsoft hazel desktop - english (great britain)';
    })[0];
    msg.volume = 1; // 0 to 1
    msg.rate = 1; // 0.1 to 10
    msg.pitch = 2; //0 to 2
    msg.text = message;
    msg.lang = 'en-US';

    speechSynthesis.speak(msg);
    this.localStorageService.SetItem('justsaid', Crypto.encrypt('1'));
  }
}
